import { useTranslation } from "@opendash/i18n";
import { Flex, InputNumber, Select, Typography } from "antd";
import { DefaultOptionType } from "antd/es/cascader";
import { observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";

type Props = {
  value?: number;
  onChange?: (value: number) => void;
  label?: string;
};

export const TimeScaleValues = {
  weeks: 604800000, // one week
  days: 86400000, // one day
  hours: 3600000, // one hour
};

export const DurationInput = observer((props: Props) => {
  const t = useTranslation();
  const [number, setNumber] = useState(0);
  const [timeScale, setTimeScale] = useState<number>(TimeScaleValues.weeks);

  function handleNumberChange(value: number) {
    setNumber(Math.round(value));
    props.onChange && props.onChange(value * timeScale);
  }

  function handleTimeScaleChange(value: number) {
    setTimeScale(value);
    props.onChange && props.onChange(number * value);
  }

  // Converts a timestamp to a number and time scale
  function convertTimestampToNumberAndTimeScale(timestamp: number) {
    // if possible, keep the time scale as it is
    if (timestamp % timeScale === 0)
      return {
        number: timestamp / timeScale,
        timeScale: timeScale,
      };

    let number = 0;
    let newTimeScale = TimeScaleValues.days;
    for (const [key, timeScaleValue] of Object.entries(TimeScaleValues)) {
      if (timestamp && timestamp % timeScaleValue === 0) {
        number = timestamp / timeScaleValue;
        newTimeScale = timeScaleValue;
        break;
      }
    }

    return { number, timeScale: newTimeScale };
  }

  // get the time scale options by the time scale values
  const timeScaleOptions: DefaultOptionType[] = [];
  for (const [key, value] of Object.entries(TimeScaleValues)) {
    timeScaleOptions.push({
      label: t(
        `maintenance:administration.general.ticket_lifetime.time_scale.${key}`
      ),
      value: value,
    });
  }

  useEffect(() => {
    const { number: convertedNumber, timeScale: convertedTimeScale } =
      convertTimestampToNumberAndTimeScale(props.value);
    setNumber(convertedNumber);
    setTimeScale(convertedTimeScale);
  }, [props.value]);

  return (
    <Flex vertical>
      {props.label && <Typography.Text strong>{props.label}</Typography.Text>}
      <Flex gap="8px" align="center">
        <InputNumber
          value={number}
          onChange={handleNumberChange}
          min={0}
          step={1}
        />
        <Select
          options={timeScaleOptions}
          style={{ width: "100px" }}
          onChange={handleTimeScaleChange}
          value={timeScale}
        />
      </Flex>
    </Flex>
  );
});
